import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import Seo from "@/components/Seo";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";
import {
  ArrowRight, Check, Phone, Zap, CalendarClock, Gauge, GraduationCap, CreditCard, Loader2,
  CalendarCheck, Gift, Award, FileCheck2, Monitor, Moon, RotateCcw, ShieldCheck, Sparkles,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FORMULES = [
  { name: "5h en accéléré", price: "750 €", lookup_key: "accelere_5h", features: ["5 leçons de conduite", "Idéal pour repasser rapidement", "Programme intensif", "Encadrement renforcé"] },
  { name: "13h en accéléré", price: "1 299 €", lookup_key: "accelere_13h", features: ["13 leçons de conduite", "Programme intensif", "Encadrement renforcé", "Rythme soutenu"] },
  { name: "20h en accéléré", price: "1 799 €", lookup_key: "accelere_20h", highlight: true, features: ["20 leçons de conduite (minimum légal)", "Le plus polyvalent", "Programme intensif", "Encadrement renforcé"] },
  { name: "30h en accéléré", price: "2 399 €", lookup_key: "accelere_30h", features: ["30 leçons de conduite", "Formation la plus complète", "Idéal débutants", "Encadrement renforcé"] },
  { name: "Permis C — Accéléré (10 jours)", price: "2 050 €", lookup_key: "accelere_permis_c", tag: "Poids Lourd", features: ["Poids lourd", "Théorie + pratique", "Programme concentré sur 10 jours"] },
  { name: "30h moto en accéléré", price: "1 400 €", lookup_key: "accelere_moto_30h", tag: "Moto", features: ["30 leçons de conduite moto", "Programme intensif", "Encadrement renforcé", "Rythme soutenu"] },
];

const AVANTAGES = [
  { icon: CalendarCheck, title: "Planning prioritaire",           desc: "Vos leçons sont programmées en priorité pour vous permettre de progresser rapidement, selon vos disponibilités et celles de nos moniteurs." },
  { icon: Gift,          title: "Heure d'évaluation offerte",     desc: "Le bilan initial (obligatoire avant inscription — 50 €) est intégralement offert avec les formules accélérées." },
  { icon: Award,         title: "Heure d'examen offerte",         desc: "L'heure de conduite associée à votre passage à l'examen pratique est offerte — un service inclus dans le pack." },
  { icon: FileCheck2,    title: "Frais administratifs offerts",   desc: "Frais d'inscription et frais de dossier ANTS entièrement pris en charge par l'auto-école." },
  { icon: ShieldCheck,   title: "Commande du permis offerte",     desc: "Après votre réussite, nous prenons en charge la commande officielle de votre permis auprès de l'ANTS." },
  { icon: Monitor,       title: "5 h de simulateur offertes",     desc: "Cinq heures sur notre simulateur de conduite nouvelle génération pour renforcer vos automatismes." },
  { icon: Moon,          title: "Soirs & dimanches sans majoration", desc: "Aucun frais supplémentaire pour les leçons après 20h ou le dimanche — un privilège exclusif de nos formules accélérées." },
  { icon: RotateCcw,     title: "Annulation flexible 24h",        desc: "Modifiez ou annulez une leçon jusqu'à 24 h à l'avance (contre 48 h en formule classique)." },
];

const SITE = "https://ddsbillancourt.fr";

export default function Accelere() {
  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Accueil", item: `${SITE}/` },
          { "@type": "ListItem", position: 2, name: "Formations accélérées", item: `${SITE}/accelere` },
        ],
      },
      {
        "@type": "Service",
        name: "Permis accéléré Boulogne-Billancourt",
        serviceType: "Formation accélérée au permis de conduire",
        provider: { "@id": `${SITE}/#school` },
        areaServed: ["Boulogne-Billancourt", "Paris", "Hauts-de-Seine", "Île-de-France"],
        offers: FORMULES.filter((f) => f.lookup_key).map((f) => ({
          "@type": "Offer",
          name: f.name,
          price: String(f.price.replace(/[^\d]/g, "")),
          priceCurrency: "EUR",
        })),
      },
    ],
  };

  return (
    <div className="min-h-screen bg-[#f8faff] text-[#1a2332] font-body" data-testid="page-accelere">
      <Seo
        title="Permis accéléré Boulogne-Billancourt · 8 avantages inclus | DDSBillancourt"
        description="Formations accélérées à Boulogne-Billancourt : 8 avantages exclusifs offerts (évaluation, heure d'examen, frais admin, 5h de simulateur, commande du permis, soirs & dimanches sans majoration, annulation 24h). Formules 5h à 750 €, 13h, 20h, 30h et Poids Lourd."
        path="/accelere"
        jsonLd={jsonLd}
      />
      <Navbar />

      {/* Hero */}
      <section className="relative bg-[#1a2332] text-white overflow-hidden">
        <div className="absolute inset-0 opacity-[0.07] pointer-events-none" style={{ backgroundImage: "radial-gradient(circle at 20% 20%, #FF8C42 0, transparent 45%), radial-gradient(circle at 80% 60%, #00a86b 0, transparent 45%)" }} />
        <div className="relative max-w-6xl mx-auto px-6 md:px-8 pt-28 md:pt-32 pb-16 md:pb-20">
          <div className="flex w-fit items-center gap-2 bg-[#FF8C42]/15 text-[#FF8C42] font-headings font-bold text-xs uppercase tracking-[0.25em] px-3 py-1.5 rounded-full">
            <Zap className="w-3.5 h-3.5" /> Formations accélérées
          </div>
          <h1 className="mt-5 font-headings font-black text-4xl md:text-6xl lg:text-7xl tracking-tight leading-[1.02] max-w-4xl" data-testid="accelere-title">
            Votre permis en <span className="text-[#FF8C42]">un temps record</span>.
          </h1>
          <p className="mt-6 text-white/80 max-w-2xl text-base md:text-lg leading-relaxed">
            Un besoin urgent pour un job, un déménagement, un projet ? Nos formations accélérées concentrent
            votre apprentissage sur quelques semaines, avec 8 avantages exclusifs inclus dans chaque pack.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <a href="#formules" className="inline-flex items-center gap-2 bg-[#FF8C42] hover:bg-[#e8763a] text-white font-headings font-bold rounded-xl px-6 py-3.5 text-sm shadow-lg transition-colors" data-testid="hero-cta-formules">
              Voir les formules <ArrowRight className="w-4 h-4" />
            </a>
            <a href="tel:+33141315061" className="inline-flex items-center gap-2 border-2 border-white/25 hover:border-[#00a86b] hover:text-[#00a86b] font-headings font-bold rounded-xl px-6 py-3.5 text-sm transition-colors" data-testid="hero-cta-tel">
              <Phone className="w-4 h-4" /> 01 41 31 50 61
            </a>
          </div>

          {/* Key numbers */}
          <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl">
            {[
              { n: "8", u: "avantages", l: "Exclusifs offerts" },
              { n: "5h", u: "simulateur", l: "Offert dans chaque pack" },
              { n: "0 €", u: "supplément", l: "Soirs & dimanches" },
              { n: "24 h", u: "annulation", l: "Flexibilité maximale" },
            ].map((k, i) => (
              <div key={i} className="bg-white/5 border border-white/10 rounded-xl px-4 py-3 backdrop-blur-sm">
                <div className="font-headings font-black text-2xl md:text-3xl text-[#FF8C42]">{k.n} <span className="text-white/70 text-sm font-bold">{k.u}</span></div>
                <div className="text-xs text-white/60 mt-1">{k.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* AVANTAGES — big prominent section */}
      <section className="relative py-16 md:py-24 bg-white" data-testid="accelere-avantages">
        <div className="max-w-6xl mx-auto px-6 md:px-8">
          <div className="max-w-2xl">
            <div className="text-[#00a86b] font-headings font-bold text-sm uppercase tracking-[0.2em]">Ce qui rend nos formules uniques</div>
            <h2 className="mt-3 font-headings font-black text-3xl md:text-5xl tracking-tight leading-[1.05]">
              8 avantages exclusifs, <span className="text-[#00a86b]">inclus</span> dans chaque pack accéléré.
            </h2>
            <p className="mt-4 text-[#5b6577] text-base md:text-lg">
              Contrairement à la formule classique, nos formations accélérées incluent gratuitement tous les
              services qui font vraiment la différence. Chaque euro dépensé est un euro qui vous rapproche du permis.
            </p>
          </div>

          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {AVANTAGES.map((a, i) => (
              <div key={a.title} data-testid={`avantage-${i}`} className="relative bg-white border-2 border-black/5 rounded-2xl p-5 md:p-6 shadow-sm hover:shadow-lg hover:border-[#FF8C42]/50 hover:-translate-y-0.5 transition-all">
                <div className="absolute top-4 right-4 w-6 h-6 rounded-full bg-[#00a86b] text-white flex items-center justify-center shrink-0" aria-label="Inclus">
                  <Check className="w-3.5 h-3.5" />
                </div>
                <div className="w-12 h-12 rounded-xl bg-[#FF8C42]/10 flex items-center justify-center">
                  <a.icon className="w-6 h-6 text-[#FF8C42]" />
                </div>
                <div className="mt-4 font-headings font-black text-base md:text-lg leading-snug pr-8">{a.title}</div>
                <p className="mt-2 text-sm text-[#5b6577] leading-relaxed">{a.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FORMULES */}
      <section id="formules" className="py-16 md:py-24 bg-[#f8faff]" data-testid="accelere-formules">
        <div className="max-w-6xl mx-auto px-6 md:px-8">
          <div className="max-w-2xl">
            <div className="text-[#FF8C42] font-headings font-bold text-sm uppercase tracking-[0.2em]">Nos formules accélérées</div>
            <h2 className="mt-3 font-headings font-black text-3xl md:text-5xl tracking-tight leading-[1.05]">
              Choisissez le rythme qui vous correspond.
            </h2>
            <p className="mt-4 text-[#5b6577] text-base md:text-lg">
              De 5 heures pour repasser après un échec, à 30 heures pour un apprentissage complet en un mois : chaque formule inclut les 8 avantages ci-dessus.
            </p>
          </div>

          <div className="mt-12 grid md:grid-cols-2 gap-5">
            {FORMULES.map((item) => (
              <FormuleCard key={item.name} item={item} />
            ))}
          </div>

          <div className="mt-8 text-center text-sm text-[#5b6577] font-body italic">
            1 leçon = 55 minutes · Paiement en 3× ou 4× sans frais disponible
          </div>
        </div>
      </section>

      {/* Comparatif */}
      <section className="py-16 md:py-20 bg-white" data-testid="accelere-compare">
        <div className="max-w-5xl mx-auto px-6 md:px-8">
          <div className="max-w-2xl mx-auto text-center">
            <div className="text-[#00a86b] font-headings font-bold text-sm uppercase tracking-[0.2em]">Accéléré vs Classique</div>
            <h2 className="mt-3 font-headings font-black text-3xl md:text-4xl tracking-tight">Pourquoi choisir la formule accélérée ?</h2>
          </div>

          <div className="mt-10 grid md:grid-cols-2 gap-5">
            <div className="bg-[#f8faff] border border-black/5 rounded-2xl p-6 md:p-8">
              <div className="text-xs font-headings font-bold uppercase tracking-[0.2em] text-[#5b6577]">Formule classique</div>
              <div className="mt-2 font-headings font-black text-2xl">Rythme libre</div>
              <ul className="mt-5 space-y-3 text-sm text-[#3a4557]">
                {["Rythme d'apprentissage étalé", "Heure d'évaluation à 50 €", "Heure d'examen facturée", "Frais admin en supplément", "Commande du permis à votre charge", "Simulateur facturé à l'heure", "Majoration +5 €/h après 20h", "Annulation 48 h avant"].map((t) => (
                  <li key={t} className="flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full bg-[#5b6577] mt-1.5 shrink-0" /> {t}</li>
                ))}
              </ul>
            </div>

            <div className="bg-[#1a2332] text-white rounded-2xl p-6 md:p-8 border-2 border-[#FF8C42] shadow-xl">
              <div className="text-xs font-headings font-bold uppercase tracking-[0.2em] text-[#FF8C42]">Formule accélérée</div>
              <div className="mt-2 font-headings font-black text-2xl">Sprint vers le permis</div>
              <ul className="mt-5 space-y-3 text-sm">
                {["Programme intensif concentré", "Évaluation offerte", "Heure d'examen offerte", "Frais admin offerts", "Commande du permis offerte", "5 h de simulateur offertes", "Aucune majoration soir & dimanche", "Annulation 24 h avant"].map((t) => (
                  <li key={t} className="flex items-start gap-2"><Check className="w-4 h-4 text-[#00a86b] mt-0.5 shrink-0" /> {t}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="pb-20 md:pb-28 bg-[#f8faff]">
        <div className="max-w-4xl mx-auto px-6 md:px-8">
          <div className="bg-[#1a2332] text-white rounded-2xl px-6 py-10 md:px-12 md:py-14 text-center shadow-2xl" data-testid="accelere-contact-band">
            <div className="inline-flex w-fit mx-auto items-center gap-2 bg-[#FF8C42]/15 text-[#FF8C42] font-headings font-bold text-xs uppercase tracking-[0.25em] px-3 py-1.5 rounded-full">
              <Sparkles className="w-3.5 h-3.5" /> Prêt à démarrer ?
            </div>
            <div className="mt-4 font-headings font-black text-2xl md:text-4xl leading-tight">Parlons de votre projet et de vos délais.</div>
            <p className="mt-3 text-white/70 max-w-2xl mx-auto">
              Nos conseillers vous rappellent sous 24 h ouvrées avec un planning personnalisé et une date
              d'examen indicative. Bureau au 37 Av. Édouard Vaillant, 92100 Boulogne-Billancourt · Conduite 7j/7 de 7h à 22h.
            </p>
            <div className="mt-7 flex flex-wrap gap-3 justify-center">
              <a href="tel:+33141315061" className="inline-flex items-center gap-2 bg-[#00a86b] hover:bg-[#00915d] text-white font-headings font-bold rounded-xl px-6 py-3.5 text-sm transition-colors" data-testid="accelere-call">
                <Phone className="w-4 h-4" /> 01 41 31 50 61
              </a>
              <Link to="/reservation" className="inline-flex items-center gap-2 border-2 border-white/20 hover:border-[#FF8C42] hover:text-[#FF8C42] font-headings font-bold rounded-xl px-6 py-3.5 text-sm transition-colors" data-testid="accelere-reservation">
                Réserver ma première leçon <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}


function FormuleCard({ item }) {
  const [loading, setLoading] = useState(false);
  const slug = item.name.slice(0, 20).replace(/\s+/g, "-").toLowerCase();

  const handlePay = async () => {
    if (!item.lookup_key || loading) return;
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/payments/checkout`, {
        lookup_key: item.lookup_key,
        origin_url: window.location.origin,
      });
      if (data?.checkout_url) window.location.href = data.checkout_url;
    } catch {
      alert("Erreur de paiement. Merci de réessayer ou de nous appeler au 01 41 31 50 61.");
      setLoading(false);
    }
  };

  return (
    <div
      data-testid={`accelere-item-${slug}`}
      className={`relative bg-white rounded-2xl p-6 md:p-8 border-2 flex flex-col ${
        item.highlight ? "border-[#FF8C42] shadow-xl" : "border-black/5 shadow-sm hover:border-[#00a86b]/40 hover:shadow-lg"
      }`}
    >
      {item.highlight && (
        <div className="absolute -top-3 left-6 bg-[#FF8C42] text-white font-headings font-bold text-xs tracking-widest uppercase px-3 py-1 rounded-full shadow-md">
          Le plus choisi
        </div>
      )}
      {item.tag && !item.highlight && (
        <div className="absolute -top-3 left-6 bg-[#1a2332] text-white font-headings font-bold text-xs tracking-widest uppercase px-3 py-1 rounded-full shadow-md">
          {item.tag}
        </div>
      )}
      <div className="flex items-start justify-between gap-6">
        <div className="flex-1">
          <h3 className="font-headings font-bold text-lg md:text-xl leading-snug">{item.name}</h3>
          <ul className="mt-4 space-y-2">
            {item.features.map((f) => (
              <li key={f} className="flex items-center gap-2 text-sm text-[#5b6577]">
                <Check className="w-4 h-4 text-[#00a86b] shrink-0" />
                {f}
              </li>
            ))}
          </ul>
        </div>
        <div className="text-right">
          <div className={`font-headings font-black text-3xl md:text-4xl tracking-tight ${item.highlight ? "text-[#FF8C42]" : "text-[#00a86b]"}`}>
            {item.price}
          </div>
          <div className="text-xs text-[#5b6577] mt-1">TTC</div>
        </div>
      </div>

      <div className="mt-5 pt-5 border-t border-dashed border-black/10">
        <div className="text-xs font-headings font-bold uppercase tracking-widest text-[#00a86b] flex items-center gap-1.5">
          <Sparkles className="w-3.5 h-3.5" /> 8 avantages inclus
        </div>
        <div className="mt-2 text-xs text-[#5b6577]">
          Évaluation + examen + frais admin + commande du permis + 5 h simulateur + soirs & dimanches sans majoration + annulation 24 h.
        </div>
      </div>

      <div className="mt-6 flex flex-col sm:flex-row gap-2">
        {item.lookup_key && (
          <button
            type="button"
            onClick={handlePay}
            disabled={loading}
            data-testid={`accelere-pay-${slug}`}
            className={`flex-1 inline-flex items-center justify-center gap-2 font-headings font-bold rounded-xl px-5 py-3 text-sm transition-colors ${
              item.highlight
                ? "bg-[#FF8C42] hover:bg-[#e8763a] text-white shadow-md disabled:opacity-70"
                : "bg-[#00a86b] hover:bg-[#00915d] text-white shadow-sm disabled:opacity-70"
            }`}
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}
            {loading ? "Redirection…" : "Payer en ligne"}
          </button>
        )}
        <Link
          to="/reservation"
          data-testid={`accelere-cta-${slug}`}
          className={`${item.lookup_key ? "sm:flex-none sm:px-5" : "flex-1"} inline-flex items-center justify-center gap-2 font-headings font-bold rounded-xl px-5 py-3 text-sm transition-colors border-2 border-[#1a2332]/15 text-[#1a2332] hover:border-[#00a86b] hover:text-[#00a86b]`}
        >
          Nous contacter <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </div>
  );
}
